import { useState } from 'react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { clausulaDeHonorarios, lerValorEmReais, valorPorExtenso } from '@/lib/honorarios';

/** Campos de honorários que o formulário edita. O JSONB pode ter outros
 *  (`moeda`, que saiu do menu de variáveis mas segue resolvível para quem já a
 *  citava no próprio modelo, e o que vier a existir), e eles são preservados no
 *  salvamento. */
const CAMPOS_HONORARIOS = ['valor', 'valor_extenso', 'forma_pagamento', 'percentual_exito', 'condicoes'] as const;

/** O JSONB não tem shape garantido: número gravado por API vira texto na tela, e
 *  não uma caixa vazia que o próximo Salvar apagaria. */
const texto = (valor: unknown) => (valor == null || typeof valor === 'object' ? '' : String(valor));

/** Tem preço? Mesma regra do backend (`honorarios_configurados`): o "como se
 *  paga" não substitui o "quanto". */
export function temPreco(honorarios: Record<string, unknown> | null | undefined): boolean {
  return ['valor', 'percentual_exito'].some((c) => texto(honorarios?.[c]).trim() !== '');
}

/** A cláusula de honorários gravados, como sai no contrato. */
export function clausulaGravada(honorarios: Record<string, unknown> | null | undefined): string {
  return clausulaDeHonorarios(
    Object.fromEntries(CAMPOS_HONORARIOS.map((campo) => [campo, texto(honorarios?.[campo])])),
  );
}

export interface HonorariosForm {
  valores: Record<string, string>;
  editar: (campo: string, valor: string) => void;
  extensoAutomatico: string | null;
  clausula: string;
  temPreco: boolean;
  /** O JSONB a gravar: os campos com conteúdo + os que o formulário não conhece. */
  paraSalvar: () => Record<string, unknown> | null;
  /** A pessoa mexeu em algum campo? (É o que o auto-save consulta.) */
  alterado: boolean;
  /** Gravado: o que está na tela passa a ser o estado salvo. */
  marcarSalvo: () => void;
}

/** Estado do formulário de honorários — da área ou os gerais do advogado. */
export function useHonorariosForm(gravados: Record<string, unknown> | null | undefined): HonorariosForm {
  const [valores, setValores] = useState<Record<string, string>>(() => {
    const carregados = Object.fromEntries(CAMPOS_HONORARIOS.map((campo) => [campo, texto(gravados?.[campo])]));
    // Extenso gravado que é só o derivado do valor gravado não é dado do
    // advogado: tratado como tal, sobreviveria à troca ou à remoção do valor e
    // iria para o contrato (e para a fala do agente) descrevendo um preço que
    // não existe mais.
    const lido = lerValorEmReais(carregados.valor ?? '');
    if (lido != null && carregados.valor_extenso === valorPorExtenso(lido)) carregados.valor_extenso = '';
    return carregados;
  });

  // Valor em reais vira extenso sozinho: o contrato escreve "R$ 1.500,00 (mil e
  // quinhentos reais)", e pedir que o advogado digite a mesma coisa duas vezes
  // era convite a divergir. Só quando o valor não é um montante ("um salário
  // mínimo") o extenso volta a ser dele.
  const valorLido = lerValorEmReais(valores.valor ?? '');
  const extensoAutomatico = valorLido != null ? valorPorExtenso(valorLido) : null;
  // Sem valor não há o que escrever por extenso: o que estivesse no campo não
  // vai para o contrato.
  const efetivos: Record<string, string> = {
    ...valores,
    valor_extenso: valores.valor?.trim() ? (extensoAutomatico ?? valores.valor_extenso ?? '') : '',
  };

  const paraSalvar = () => {
    // O PUT substitui o JSONB inteiro, então o que este formulário não conhece
    // precisa viajar junto — senão salvar apagaria `moeda` (que está no catálogo
    // de variáveis e o modelo do advogado pode citar), em silêncio. Campo vazio
    // some de propósito: `""` responderia "sim" à pergunta "tem preço?" que o
    // backend faz olhando o conteúdo.
    const outros = Object.fromEntries(
      Object.entries(gravados ?? {}).filter(
        ([campo]) => !CAMPOS_HONORARIOS.includes(campo as (typeof CAMPOS_HONORARIOS)[number]),
      ),
    );
    const preenchidos = {
      ...outros,
      ...Object.fromEntries(
        Object.entries(efetivos)
          .map(([campo, valor]) => [campo, valor.trim()])
          .filter(([, valor]) => valor !== ''),
      ),
    };
    return Object.keys(preenchidos).length > 0 ? preenchidos : null;
  };

  // "Alterado" é ter DIGITADO, não diferir do gravado. A comparação com o
  // gravado acusava diferença sem ninguém tocar em nada (valor sem o extenso
  // derivado, número gravado pela API) e o auto-save regravava ao navegar — com
  // toast de "salvo" para quem nem abriu o cartão.
  const [alterado, setAlterado] = useState(false);

  return {
    valores,
    editar: (campo, valor) => {
      setAlterado(true);
      setValores((prev) => ({ ...prev, [campo]: valor }));
    },
    extensoAutomatico,
    clausula: clausulaDeHonorarios(efetivos),
    temPreco: ['valor', 'percentual_exito'].some((c) => valores[c]?.trim()),
    paraSalvar,
    alterado,
    marcarSalvo: () => setAlterado(false),
  };
}

interface HonorariosCamposProps {
  form: HonorariosForm;
  /** Prefixo dos ids dos campos (há dois formulários na mesma tela). */
  idPrefixo: string;
}

/** Os campos de honorários + a prévia da cláusula do contrato. */
export function HonorariosCampos({ form, idPrefixo }: HonorariosCamposProps) {
  const { valores, editar, extensoAutomatico, clausula } = form;
  const id = (campo: string) => `${idPrefixo}-${campo}`;

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor={id('valor')}>Valor fixo</Label>
        <Input
          id={id('valor')}
          value={valores.valor}
          onChange={(e) => editar('valor', e.target.value)}
          placeholder="Ex.: R$ 1.500,00"
          maxLength={60}
        />
        <p className="text-xs text-muted-foreground">
          Devido independentemente do resultado (entrada, pró-labore).
          {extensoAutomatico && <> No contrato: “{valores.valor?.trim()} ({extensoAutomatico})”.</>}
        </p>
      </div>

      {/* Extenso só se pede quando o valor não é um montante em reais
          ("um salário mínimo"): aí não há como escrevê-lo por ele. */}
      {!extensoAutomatico && valores.valor?.trim() && (
        <div className="space-y-1.5">
          <Label htmlFor={id('extenso')}>Valor fixo por extenso</Label>
          <Input
            id={id('extenso')}
            value={valores.valor_extenso}
            onChange={(e) => editar('valor_extenso', e.target.value)}
            placeholder="Ex.: um salário mínimo vigente"
            maxLength={120}
          />
          <p className="text-xs text-muted-foreground">
            O contrato escreve o valor também por extenso, entre parênteses.
          </p>
        </div>
      )}

      <div className="space-y-1.5">
        <Label htmlFor={id('exito')}>Percentual de êxito</Label>
        <Input
          id={id('exito')}
          value={valores.percentual_exito}
          onChange={(e) => editar('percentual_exito', e.target.value)}
          placeholder="Ex.: 30% sobre o proveito econômico"
          maxLength={80}
        />
        <p className="text-xs text-muted-foreground">
          Devido só em caso de êxito. Com valor fixo preenchido, os dois se somam.
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={id('forma')}>Forma de pagamento do valor fixo</Label>
        <Input
          id={id('forma')}
          value={valores.forma_pagamento}
          onChange={(e) => editar('forma_pagamento', e.target.value)}
          placeholder="Ex.: em 3 parcelas iguais, via PIX"
          maxLength={200}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={id('condicoes')}>Condições</Label>
        <Textarea
          id={id('condicoes')}
          value={valores.condicoes}
          onChange={(e) => editar('condicoes', e.target.value)}
          placeholder="Ex.: custas e despesas processuais correm por conta do contratante."
          rows={3}
          maxLength={600}
        />
      </div>

      {clausula && (
        <div className="space-y-1 rounded-lg border bg-muted/40 p-3">
          <p className="text-xs font-medium text-muted-foreground">Como fica no contrato</p>
          <p className="text-sm">{clausula}</p>
        </div>
      )}
    </div>
  );
}
